// FAQ builders for every public page. Answers are derived from the live
// content tree wherever possible, so admin edits stay reflected. Each list
// is rendered visibly and exposed as schema.org FAQPage JSON-LD so AI agents
// and search engines can read it.

const plain = (value) => {
  if (value && typeof value === "object" && "text" in value) return value.text || "";
  return value || "";
};

const oneLine = (value) => plain(value).replace(/\n/g, " ").trim();

const joinList = (items) => (items || []).map((item) => oneLine(item).replace(/\.$/, "")).join("; ") + ".";

const contactSentence = (c) => {
  const cb = c.global.contactBar;
  return `Book a discovery call through any booking button on the site, send a message through the contact form, email ${cb.email} or call ${cb.phone}.`;
};

export const buildHomeFaqs = (c) => {
  const umbrellas = c.services.umbrellas || [];
  const names = umbrellas.map((u) => oneLine(u.title));
  const serviceCount = umbrellas.reduce((sum, u) => sum + (u.services || []).length, 0);
  return [
    {
      q: "What does IT Device Services do?",
      a: `IT Device Services is an endpoint lifecycle management company. We plan, deploy, manage, refresh and retire business devices through ${names.length} service areas: ${names.join(", ")}, covering ${serviceCount} services across the full device lifecycle.`,
    },
    {
      q: "What service areas does IT Device Services offer?",
      a: umbrellas
        .map((u) => `${oneLine(u.title)} (${oneLine(u.tagline).toLowerCase()}): ${(u.services || []).map((s) => oneLine(s.title)).join(", ")}.`)
        .join(" "),
    },
    {
      q: "Which platforms and devices do you support?",
      a: "We manage Windows and macOS devices throughout their entire lifecycle, from provisioning and deployment through ongoing management, technology refresh and secure disposal.",
    },
    {
      q: "How do I get started with IT Device Services?",
      a: contactSentence(c),
    },
    {
      q: "Where is IT Device Services located and when are you available?",
      a: `We are based at ${oneLine(c.contact.address)} and available ${oneLine(c.contact.hours)}. Replies to messages arrive within one business day.`,
    },
  ];
};

export const buildServicesHubFaqs = (c) => {
  const umbrellas = c.services.umbrellas || [];
  return [
    {
      q: "What is the difference between Managed Services and Project Services?",
      a: "Managed Services are ongoing: continuous device, patch and provisioning management that keeps your endpoint environment secure and compliant month after month. Project Services are one-time engagements with a defined start and finish, such as onboarding, migration, technology refresh or hardware disposal.",
    },
    {
      q: "What is included in each service area?",
      a: umbrellas
        .map((u) => `${oneLine(u.title)}: ${(u.services || []).map((s) => oneLine(s.title)).join(", ")}.`)
        .join(" "),
    },
    {
      q: "How does Technology Consulting fit alongside the other services?",
      a: "Technology Consulting provides the strategic layer: technology assessments, endpoint strategy, roadmaps and procurement guidance that make sure every managed service and project supports your business goals rather than just adding tools.",
    },
    {
      q: "Can I combine services or start with just one?",
      a: `Yes. Every service works standalone and most clients combine them over time, for example a Device Onboarding project followed by ongoing Device Management. ${contactSentence(c)}`,
    },
  ];
};

export const buildServiceFaqs = (umbrella, service, c) => [
  {
    q: `What is ${oneLine(service.title)} from IT Device Services?`,
    a: oneLine(service.teaser),
  },
  {
    q: `How does IT Device Services help with ${oneLine(service.title)}?`,
    a: joinList(service.howWeHelp),
  },
  {
    q: `Who is ${oneLine(service.title)} ideal for?`,
    a: `It is a strong fit for: ${joinList(service.idealFor)}`,
  },
  {
    q: `Is ${oneLine(service.title)} an ongoing service or a one-time project?`,
    a: `${oneLine(service.title)} is part of our ${oneLine(umbrella.title)} group (${oneLine(umbrella.tagline).toLowerCase()}). ${contactSentence(c)}`,
  },
];

export const buildAboutFaqs = (c) => [
  {
    q: "Who is IT Device Services?",
    a: oneLine(c.about.intro),
  },
  {
    q: "How does IT Device Services work with clients?",
    a: "We work as an extension of your team with a simple, repeatable way of working that keeps projects predictable and day-to-day operations quiet. Whether you have no internal IT function or an experienced team that needs specialist support, we scale to fit and hand back a clean, documented environment.",
    },
  {
    q: "Does IT Device Services replace my internal IT team?",
    a: "No. We complement internal teams by taking on endpoint lifecycle work, ongoing device management, projects and strategy, so your people can focus on the business instead of device administration.",
  },
  {
    q: "How do I start working with IT Device Services?",
    a: contactSentence(c),
  },
];

export const buildContactFaqs = (c) => {
  const cb = c.global.contactBar;
  return [
    {
      q: "How quickly does IT Device Services respond to messages?",
      a: "We reply to every message within one business day, with no sales pressure, just straight answers.",
    },
    {
      q: "What are your business hours?",
      a: `Our team is available ${oneLine(c.contact.hours)}.`,
    },
    {
      q: "How can I reach IT Device Services directly?",
      a: `Email ${cb.email} or call ${cb.phone}. We are based at ${oneLine(c.contact.address)}.`,
    },
    {
      q: "Can I book a call instead of sending a message?",
      a: "Yes. Every booking button on the site opens our scheduling calendar where you can pick a discovery call slot that suits you.",
    },
    {
      q: "What should I include in my message?",
      a: "A little about your environment helps us respond usefully: roughly how many devices you run, your current setup and any timelines you are working toward. Your details stay private.",
    },
  ];
};

export const buildBlogFaqs = (c) => [
  {
    q: "What topics does the IT Device Services blog cover?",
    a: oneLine(c.blog.intro),
  },
  {
    q: "Who writes the articles on this blog?",
    a: "Our own team writes every article. Everything we write about is something we actually do for clients, from device refresh planning to patch management.",
  },
  {
    q: "Can IT Device Services implement what the articles describe?",
    a: `Yes. Each article maps to a service we deliver. ${contactSentence(c)}`,
  },
];

const blogPostFaqsBySlug = {
  "what-is-endpoint-lifecycle-management": [
    {
      q: "What is endpoint lifecycle management?",
      a: "It is the deliberate management of every stage a business device moves through: provisioning and onboarding, ongoing patching and administration, technology refresh, and eventually secure disposal, instead of handling each stage reactively and separately.",
    },
    {
      q: "What are the three service areas that make up the lifecycle?",
      a: "Managed Services (Device Management, Patch Management, Provisioning & Deployment) covers the ongoing day-to-day work. Project Services (Device Onboarding, Device Migration, Device Provisioning, Technology Refresh, Hardware Disposal) covers the discrete projects that move a device between stages. Technology Consulting (Technology Assessments, Endpoint Strategy, Technology Roadmaps, Technology Procurement Guidance) covers the strategy behind the decisions.",
    },
    {
      q: "Why does managing the lifecycle end to end matter?",
      a: "Gaps between stages, such as a device that is onboarded but never patched consistently, are what cause rising support tickets, security exposure and unpredictable costs. Managing the full lifecycle gives every device a known state at every point, which is what lets a business plan instead of react.",
    },
  ],
  "device-management-vs-provisioning-whats-the-difference": [
    {
      q: "What is the difference between Device Management and Provisioning & Deployment?",
      a: "Provisioning & Deployment is the one-time preparation of a device before it reaches an employee: imaging, installing applications, applying security settings. Device Management is the ongoing administration that keeps a device secure, compliant and healthy for as long as it stays in service.",
    },
    {
      q: "Do I need both services?",
      a: "Most businesses do. Provisioning gets a device off to a good start, and Device Management makes sure it stays that way. The two are frequently paired so a new machine is deployed correctly and then immediately enrolled into ongoing management.",
    },
    {
      q: "When does Provisioning & Deployment happen versus Device Management?",
      a: "Provisioning & Deployment happens every time you hire someone or replace a machine, with a clear start and finish. Device Management runs continuously for as long as the device is in use, with no end date.",
    },
  ],
  "planning-a-technology-refresh-without-the-downtime": [
    {
      q: "How do you plan a technology refresh without disrupting the business?",
      a: "It starts with an honest inventory of which devices are due for replacement and what needs to move with each user, then rolls out replacements in coordinated waves through Device Provisioning rather than all at once, so support capacity is never overwhelmed.",
    },
    {
      q: "What happens to the old hardware after a refresh?",
      a: "It goes through Hardware Disposal: certified data destruction, then responsible recycling or refurbishment, with documentation for every serial number. A refresh is not complete until the old hardware has actually left the building responsibly.",
    },
    {
      q: "What is the biggest cause of refresh projects running over budget?",
      a: "Skipping the upfront inventory step. Problems that should have been caught on paper, like unclear data migration needs, get discovered mid-rollout instead, which is far more expensive to fix.",
    },
  ],
  "how-a-technology-assessment-works": [
    {
      q: "What does a Technology Assessment actually look at?",
      a: "It reviews three things together: the infrastructure itself (what devices exist and their condition), the management processes around them (how patching and support actually happen day to day), and the operational impact (where employees lose time and where risk is building up).",
    },
    {
      q: "What do I get at the end of a Technology Assessment?",
      a: "A prioritized, practical set of findings rather than a report that sits in a folder: which gaps are urgent, which can wait, and what each is likely costing in time, risk or budget if left alone.",
    },
    {
      q: "What happens after the assessment?",
      a: "Endpoint Strategy and Technology Roadmaps typically pick up from there, turning the assessment's findings into a sequenced plan your budget and team can absorb, rather than making every improvement at once.",
    },
  ],
};

export const buildBlogPostFaqs = (post, c) => {
  const curated = blogPostFaqsBySlug[post.slug];
  if (curated) return curated;
  // Fallback for posts created later in the admin panel.
  return [
    {
      q: `What is "${oneLine(post.title)}" about?`,
      a: oneLine(post.excerpt),
    },
    {
      q: "Who should read this article?",
      a: "Business owners and operations leaders who want their technology to work reliably, stay secure and get out of the way, without running a data center to achieve it.",
    },
    {
      q: "Can IT Device Services help with this topic?",
      a: `Yes. Everything we write about is something we do for clients. ${contactSentence(c)}`,
    },
  ];
};

export const buildLegalFaqs = (slug, c) => {
  const cb = c.global.contactBar;
  const lists = {
    "privacy-policy": [
      {
        q: "What personal information does IT Device Services collect?",
        a: "We collect only what you choose to share with us, such as the name, email address, company, phone number and message you submit through the contact form, plus the minimal technical data needed to keep the site running.",
      },
      {
        q: "How is my information used?",
        a: "Your details are used solely to respond to your inquiry and deliver the services you request. We do not sell personal information. The full policy on this page describes usage, retention and your rights in detail.",
      },
      {
        q: "How do I ask a question about my privacy or my data?",
        a: `Email ${cb.email} or call ${cb.phone} and we will respond within one business day.`,
      },
    ],
    "terms-of-service": [
      {
        q: "What do these Terms of Service cover?",
        a: "They govern your use of the IT Device Services website and the engagement terms for our services, including acceptable use, intellectual property and liability, as set out in the sections on this page.",
      },
      {
        q: "How do I accept the Terms of Service?",
        a: "Using this website or engaging our services constitutes acceptance of these terms. Individual client engagements are additionally covered by their own service agreements.",
      },
      {
        q: "Who do I contact with questions about these terms?",
        a: `Email ${cb.email} and our team will clarify anything in these terms within one business day.`,
      },
    ],
    "cookie-policy": [
      {
        q: "What cookies does this website use?",
        a: "We use a small number of cookies to keep the site running smoothly, remember your cookie consent choice and understand how the site is used, as described in the sections on this page.",
      },
      {
        q: "How do I manage or decline cookies?",
        a: "You can accept or decline cookies in the consent banner shown on your first visit, and your browser settings let you clear or block cookies at any time. Your choice is stored locally on your device.",
      },
      {
        q: "Who do I contact about cookies or tracking?",
        a: `Email ${cb.email} with any questions about cookies and we will respond within one business day.`,
      },
    ],
  };
  return lists[slug] || [];
};

// ---------------------------------------------------------------------------
// Admin editing support
// ---------------------------------------------------------------------------
// resolveFaqs returns the admin-saved override for a page key when one exists
// and is non-empty, otherwise the auto-generated fallback. This keeps every
// page populated by default while letting the client tune any answer.
export const resolveFaqs = (content, key, fallbackItems) => {
  const saved = content && content.faqs ? content.faqs[key] : undefined;
  if (Array.isArray(saved) && saved.length > 0) {
    const cleaned = saved
      .map((item) => ({
        q: (item && item.q ? String(item.q) : "").trim(),
        a: (item && item.a ? String(item.a) : "").trim(),
      }))
      .filter((item) => item.q && item.a);
    if (cleaned.length > 0) return cleaned;
  }
  return fallbackItems;
};

// buildFaqGroups enumerates every editable FAQ group in display order, each
// with its auto-generated default set. The admin panel uses this to render an
// editor for the whole site (static pages, every service, every blog post and
// the legal pages), including services and posts created later in the CMS.
export const buildFaqGroups = (c) => {
  const groups = [
    { key: "home", label: "Home Page", auto: buildHomeFaqs(c) },
    { key: "services", label: "Services Hub", auto: buildServicesHubFaqs(c) },
    { key: "about", label: "About Page", auto: buildAboutFaqs(c) },
    { key: "contact", label: "Contact Page", auto: buildContactFaqs(c) },
    { key: "blog", label: "Blog Listing", auto: buildBlogFaqs(c) },
  ];

  (c.services.umbrellas || []).forEach((umbrella) => {
    (umbrella.services || []).forEach((service) => {
      groups.push({
        key: `service:${service.slug}`,
        label: oneLine(service.title),
        section: `Service: ${oneLine(umbrella.title)}`,
        auto: buildServiceFaqs(umbrella, service, c),
      });
    });
  });

  (c.blog.posts || []).forEach((post) => {
    groups.push({
      key: `blogpost:${post.slug}`,
      label: oneLine(post.title),
      section: "Blog Posts",
      auto: buildBlogPostFaqs(post, c),
    });
  });

  const legalLabels = {
    "privacy-policy": "Privacy Policy",
    "terms-of-service": "Terms of Service",
    "cookie-policy": "Cookie Policy",
  };
  Object.keys(legalLabels).forEach((slug) => {
    groups.push({
      key: `legal:${slug}`,
      label: legalLabels[slug],
      section: "Legal Pages",
      auto: buildLegalFaqs(slug, c),
    });
  });

  return groups;
};

